import { apiRoutes } from '@/config/routes'
import type { VaultApy } from '@/types/apy'
import type { SdkNetwork } from '@/types/network'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { HOUR_IN_SECONDS, SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'

const historicalBenchmarkQueryKey = ['historicalBenchmark'] as const

interface HistoricalBenchmarkQueryParams {
  code: 'usd' | 'eth'
  network: SdkNetwork
}

export interface BenchmarkDataPoint {
  apy: VaultApy | null
  timestamp: number
}

interface HistoricalBenchmarksResponse {
  data: Record<string, BenchmarkDataPoint[]>
  loadTime: number
  fromCache: boolean
  success: boolean
  error?: string
}

export function historicalBenchmarkQueryOptions({ code, network }: HistoricalBenchmarkQueryParams) {
  return queryOptions({
    queryKey: [...historicalBenchmarkQueryKey, { code, network }] as const,
    async queryFn() {
      const params = new URLSearchParams({ code })
      const response = await fetch(`${apiRoutes.apiHistoricalBenchmarks}?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch historical benchmarks')
      }
      return response.json() as Promise<HistoricalBenchmarksResponse>
    },
    select(data) {
      if (data == null || !data.success) return null
      return (data.data[network] ?? []).filter((point) => point.apy != null)
    },
    retry: 3,
    staleTime: HOUR_IN_SECONDS * SECOND_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })
}

export const useHistoricalBenchmarkQuery = ({
  code,
  network,
  initialData,
}: HistoricalBenchmarkQueryParams & {
  initialData?: ReturnType<typeof historicalBenchmarkQueryOptions>['initialData']
}) => {
  const { data: benchmark, isFetching } = useQuery({
    ...historicalBenchmarkQueryOptions({ code, network }),
    initialData,
  })
  return { benchmark, isFetching }
}

export type HistoricalBenchmark = ReturnType<typeof useHistoricalBenchmarkQuery>['benchmark']
